import React, { useEffect, useState } from 'react';
import './MainLayout.css';
import Header from 'components/layouts/Header/Header';
import Sidebar from 'components/layouts/Sidebar/Sidebar';
import LoadingOverlay from 'components/Common/LoadingOverlay';
import OrdersScreen from 'components/Homepage/OrdersScreen';
import OriginatorScreen from 'components/OriginatorHome/OriginatorScreen';
import AdminScreen from 'components/Admin/AdminScreen';
import { NewOrderScreen } from 'components/New Orders/NewOrderScreen';
import PrivateRoute from 'components/Common/PrivateRoute';
import Login from 'components/Auth/Login';
import LoginCallback from 'components/Auth/LoginCallback';
import { useOktaAuth } from '@okta/okta-react';
import { initializeAPIwithAuthentication } from 'services/api/core';

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';

function MainLayoutContent({ version }) {
  const { authState } = useOktaAuth();
  const location = useLocation();
  const [apiInitialized, setApiInitialized] = useState(false);

  const isAuthenticated = authState?.isAuthenticated;
  const isPending = authState?.isPending;

  // Don't show loading overlay for callback route - let LoginCallback handle it
  const isCallbackRoute = location.pathname === '/login/callback';

  // Initialize the API with authentication when authenticated
  // IMPORTANT: All hooks must be called before any conditional returns
  useEffect(() => {
    let isMounted = true;

    const initAPI = async () => {
      if (isAuthenticated && !apiInitialized && isMounted) {
        try {
          await initializeAPIwithAuthentication();
          if (isMounted) {
            setApiInitialized(true);
          }
        } catch (error) {
          console.error('Failed to initialize API:', error);
        }
      }
    };

    const resetAPI = () => {
      if (!isAuthenticated && apiInitialized && isMounted) {
        // Reset API initialization state when user logs out
        setApiInitialized(false);
      }
    };

    if (isAuthenticated) {
      initAPI();
    } else {
      resetAPI();
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]); // Removed apiInitialized from dependencies to prevent loops

  // Show loading overlay while auth state is pending (except for callback)
  if (!isCallbackRoute && (!authState || isPending)) {
    return (
      <LoadingOverlay
        message="Initializing application..."
        status={
          !authState ? 'Checking authentication state' : 'Validating session'
        }
      />
    );
  }

  return (
    <>
      {/* Only show header and sidebar when authenticated and not on callback */}
      {authState?.isAuthenticated && !isCallbackRoute && (
        <>
          <Header />
          <Sidebar version={version} />
        </>
      )}
      <div
        className={
          authState?.isAuthenticated && !isCallbackRoute
            ? 'mainActionWindow'
            : 'loginWindow'
        }
      >
        <div className="actionwindow">
          <Switch>
            <Route path="/login/callback" component={LoginCallback} />
            <Route
              path="/login"
              render={() =>
                !authState?.isAuthenticated ? (
                  <Login />
                ) : (
                  <Redirect to={{ pathname: '/' }} />
                )
              }
            />
            <PrivateRoute path="/admin" requiredRoles={['Administrator']}>
              <AdminScreen />
            </PrivateRoute>
            <PrivateRoute
              path="/neworder"
              requiredRoles={['Administrator', 'HedgeDesk', 'Originator']}
            >
              <NewOrderScreen />
            </PrivateRoute>
            <PrivateRoute path="/originator" requiredRoles={['Originator']}>
              <OriginatorScreen />
            </PrivateRoute>
            <PrivateRoute
              path="/"
              requiredRoles={[
                'Administrator',
                'HedgeDesk',
                'Originator',
                'Moderator',
              ]}
            >
              <OrdersScreen />
            </PrivateRoute>
          </Switch>
        </div>
      </div>
    </>
  );
}

export default function MainLayout({ version }) {
  return (
    <Router>
      <MainLayoutContent version={version} />
    </Router>
  );
}
